/**
 * Company: SPHMedia
 * Description: [Detects availability of catfish ad unit]
 */

import { isClientSide } from "@helper/utils";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";

type useCatfishObserverData = {
  isCatfishAvailable: boolean;
};

type useCatfishObserverProps = {
  isCatfishEnabled: boolean | undefined;
  isMobile: boolean;
};

/**
 * Custom React Hook: useCatfishObserver
 * Description: [Hook to detect availability and closure of catfish ad unit]
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useCatfishObserver({
  isCatfishEnabled,
  isMobile,
}: useCatfishObserverProps): useCatfishObserverData {
  const catfishDisplayedCookie = Cookies.get("catfishDisplayed");
  const [isCatfishAvailable, setIsCatfishAvailable] = useState<boolean>(false);

  useEffect(() => {
    if (!isClientSide) return;

    // Existing logic
    if (
      isCatfishEnabled !== undefined &&
      (typeof catfishDisplayedCookie === "undefined" ||
        (window.dfp_preview_ids &&
          Array.isArray(window.dfp_preview_ids) &&
          window.dfp_preview_ids.length !== 0)) &&
      isMobile &&
      isCatfishEnabled
    ) {
      setIsCatfishAvailable(true);
    }

    let closeObserver: MutationObserver | null = null;

    // Function to attach close observer once element is present
    const attachCloseObserver = (el: HTMLElement) => {
      if (closeObserver) return; // already attached

      closeObserver = new MutationObserver(() => {
        const iframe = el.querySelector("iframe");

        const isClosed =
          !el.offsetHeight ||
          !el.offsetWidth ||
          (iframe && (iframe.offsetHeight === 0 || iframe.offsetWidth === 0)) ||
          getComputedStyle(el).display === "none";

        if (isClosed) {
          setIsCatfishAvailable(false);
          closeObserver?.disconnect();
        }
      });

      closeObserver.observe(el, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    };

    // ---- Observer to detect when #dfp-ad-catfish appears ----
    const domObserver = new MutationObserver(() => {
      const el = document.getElementById("dfp-ad-catfish");
      if (el) {
        attachCloseObserver(el);
        domObserver.disconnect(); // stop looking for element
      }
    });

    domObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // If already present on first render, attach immediately
    const existingEl = document.getElementById("dfp-ad-catfish");
    if (existingEl) attachCloseObserver(existingEl);

    return () => {
      domObserver.disconnect();
      closeObserver?.disconnect();
    };
  }, [catfishDisplayedCookie, isCatfishEnabled, isMobile]);

  return { isCatfishAvailable };
}
